import React, { useCallback, useContext, useMemo } from "react";
import { AxiosError } from "axios";
import {
  QueryResult,
  QueryKey,
  InfiniteQueryResult,
  useMutation,
  queryCache,
} from "react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "fe-shared/src/types/common";

import UserContext from "contexts/userContext";
import TransactionStatus from "components/TransactionStatus";
import TransactionNavigation from "components/TransactionNavigation";
import TransactionContentView from "components/TransactionContentView";
import { ReturnIcon } from "fe-shared/src/components/Icons";
import {
  LinkButton,
  Loader,
  CollapseComponent,
  NotFoundPage,
  RecordsSidebar,
  Banner,
} from "fe-shared/src/components";
import useDialog from "fe-shared/src/hooks/useDialog";
import {
  PermissionLevel,
  TireMaterialConfig,
  UserPermission,
} from "fe-shared/src/types/domain";
import {
  hasAnyPermission,
  hasPermission,
} from "fe-shared/src/helpers/permissions";
import {
  DialogHeader,
  InvoiceLink,
  ProcessingStatusBanner,
} from "fe-shared-transactions/src/components";
import {
  CorrectionRawResponse,
  Retreader,
  Transaction,
  TransactionStatus as TransactionStatusEnum,
  TransactionType,
} from "fe-shared-transactions/src/types";
import correctionResponseMapper from "fe-shared-transactions/src/helpers/correctionResponseMapper";

import {
  PERMISSIONS,
  TRANSACTION_ACTIVITIES_QUERY_KEY,
  TRANSACTION_DETAILS_PORTAL_ID,
} from "consts";
import RetreadingTransactionFormDialog from "../../components/RetreadingTransactionFormDialog";
import { RetreadingTransactionFormContent } from "../../types";
import TransactionActionsContainer from "./containers/TransactionActionsContainer";

import OverrideRegYear from "fe-shared-transactions/src/components/OverrideRegYear";
import ClaimNumberEditButton from "fe-shared-transactions/src/components/ClaimNumberEditButton";
import TrailerNumberEditButton from "fe-shared-transactions/src/components/TrailerNumberEditButton";

import * as api from "./modules/api";

import styles from "./TransactionDetailsView.module.scss";

const FORWARD_TRACE_TYPE_AVAILABLE_LIST: TransactionType[] = [
  TransactionType.Shipment,
  TransactionType.Pickup,
  TransactionType.Delivery,
  TransactionType.Receipt,
];

const BACKWARD_TRACE_TYPE_AVAILABLE_LIST: TransactionType[] = [
  TransactionType.Shipment,
  TransactionType.Pickup,
  TransactionType.Delivery,
  TransactionType.Receipt,
  TransactionType.Disposition,
  TransactionType.Adjustment,
];

interface Props {
  returnUrl: string;
  transaction: QueryResult<Transaction, AxiosError<ApiError>>;
  linkedOriginal: QueryResult<Transaction, AxiosError<ApiError>>;
  linkedAdjustment: QueryResult<Transaction, AxiosError<ApiError>>;
  corrections: QueryResult<CorrectionRawResponse, AxiosError<ApiError>>;
  recycleMaterials: QueryResult<TireMaterialConfig[], unknown>;
  transactionQueryKey: QueryKey;
  correctionsQueryKey: QueryKey;
  isFetching: boolean;
  isWithNavigation: boolean;
  isDirectNavigation: boolean;
  navigationTransactions: InfiniteQueryResult<
    api.NavigationTransactions,
    AxiosError<ApiError>
  >;
  transactionListInfo: QueryResult<
    api.TransactionListInfo,
    AxiosError<ApiError>
  >;
}

const TransactionDetailsView = (props: Props) => {
  const {
    returnUrl,
    transaction,
    linkedOriginal,
    linkedAdjustment,
    corrections,
    recycleMaterials,
    transactionQueryKey,
    correctionsQueryKey,
    isFetching: isFetchingProp,
    isWithNavigation,
    isDirectNavigation,
    navigationTransactions,
    transactionListInfo: transactionListInfo
  } = props;
  const {
    data,
    isLoading: isLoadingTransaction,
    isFetching: isFetchingTransaction,
    isError: isErrorTransaction,
  } = transaction;
  const {
    data: linkedAdjustmentData,
    isFetching: isFetchingLinkedAdjustment,
  } = linkedAdjustment;
  const {
    data: linkedOriginalData,
    isFetching: isFetchingLinkedOriginal,
  } = linkedOriginal;
  const {
    data: correctionsData,
    isLoading: isLoadingCorrections,
    isFetching: isFetchingCorrections,
    isError: isErrorCorrections,
  } = corrections;
  const {
    data: materials,
    isLoading: isRecycleMaterialsLoading,
    isFetching: isRecycleMaterialsFetching,
  } = recycleMaterials;
  const { t } = useTranslation();
  const currentUser = useContext(UserContext);
  const hasSidebar = hasAnyPermission(currentUser?.permissions, [
    {
      name: PERMISSIONS.transactionHistory,
      level: PermissionLevel.Read,
    },
    {
      name: PERMISSIONS.transactionNotes,
      level: PermissionLevel.Read,
    },
  ]);
  const canAssign = hasPermission(currentUser?.permissions, {
    name: PERMISSIONS.transactionDetailsAssign,
    level: PermissionLevel.Full,
  });
  const canOverrideRegYear = hasPermission(currentUser?.permissions, {
    name: PERMISSIONS.transactionOverrideRegulatoryYear,
    level: PermissionLevel.Full,
  });
  const canUpdateClaimNumber = hasPermission(currentUser?.permissions, {
    name: PERMISSIONS.transactionUpdateClaimNumber,
    level: PermissionLevel.Full,
  });
  const canUpdateTrailerNumber = hasPermission(currentUser?.permissions, {
    name: PERMISSIONS.transactionUpdateTrailerNumber,
    level: PermissionLevel.Full,
  });

  const isPermitted = (permission: UserPermission | UserPermission[]) => {
    if (Array.isArray(permission)) {
      return permission.some((item) =>
        hasPermission(currentUser?.permissions, item)
      );
    }
    return hasPermission(currentUser?.permissions, permission);
  };

  const canViewHistory = isPermitted({
    name: PERMISSIONS.transactionHistory,
    level: PermissionLevel.Read,
  });

  const canExportHistory = isPermitted({
    name: PERMISSIONS.transactionHistoryExport,
    level: PermissionLevel.Full,
  });

  const canViewNotes = isPermitted({
    name: PERMISSIONS.transactionNotes,
    level: PermissionLevel.Read,
  });
  const canAddNote = isPermitted({
    name: PERMISSIONS.transactionAddNote,
    level: PermissionLevel.Full,
  });

  const canExportNotes = isPermitted({
    name: PERMISSIONS.transactionNotesExport,
    level: PermissionLevel.Full,
  });

  const canFlagManually = isPermitted({
    name: PERMISSIONS.transactionManualFlagging,
    level: PermissionLevel.Full,
  });

  const canEditManualFlag = isPermitted({
    name: PERMISSIONS.transactionEditManualFlag,
    level: PermissionLevel.Full,
  });

  const canUnflag = isPermitted({
    name: PERMISSIONS.transactionUnflag,
    level: PermissionLevel.Full,
  });

  const canEditRetreadingTransaction = isPermitted({
    name: PERMISSIONS.transactionRetreadingEdit,
    level: PermissionLevel.Full,
  });

  const canSeeLinkedBatches = isPermitted({
    name: PERMISSIONS.transactionSeeTraceability,
    level: PermissionLevel.Read,
  });

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  const [
    editRetreadingTransaction,
    editRetreadingTransactionResult,
  ] = useMutation<
    unknown,
    AxiosError<ApiError>,
    RetreadingTransactionFormContent
  >(api.editRetreadingTransaction, {
    onSuccess: () => {
      closeDialog();
      queryCache.invalidateQueries(transactionQueryKey);
    },
  });

  const {
    isLoading: isLoadingRetreadingTransactionEditing,
    isError: isErrorRetreadingTransactionEditing,
  } = editRetreadingTransactionResult;

  const isTransactionNotFound = transaction.error?.response?.status === 404;

  const isCurrentUserAssignee = data?.assignee?.userId === currentUser?.userId;

  const isLoading =
    isLoadingTransaction ||
    isLoadingCorrections ||
    isRecycleMaterialsLoading ||
    isLoadingRetreadingTransactionEditing;
  const isError =
    isErrorTransaction ||
    isErrorCorrections ||
    isErrorRetreadingTransactionEditing;

  const mappedCorrections = useMemo(() => {
    if (correctionsData && data) {
      const isCorrectionsExist = !!Object.values(correctionsData).filter(
        Boolean
      ).length;

      return isCorrectionsExist
        ? correctionResponseMapper(correctionsData, data)
        : null;
    }

    return null;
  }, [correctionsData, data]);

  const onReTreadingEditSubmit = useCallback(
    (values: RetreadingTransactionFormContent) => {
      editRetreadingTransaction({
        ...values,
        transactionId: data!.transactionId,
      });
    },
    [data, editRetreadingTransaction]
  );

  const isInitiatedDelivery =
    data?.transactionType === TransactionType.Delivery &&
    data?.statusCode === TransactionStatusEnum.Synchronizing &&
    data?.processingStatusCode !== "InProgress";

  const renderContent = () => {
    const { transactionId, transactionType, transactionNumber } = data!;

    const activitiesQueryKey: QueryKey = [
      TRANSACTION_ACTIVITIES_QUERY_KEY,
      transactionNumber,
    ];

    const forwardTraceabilityQueryKey: QueryKey = [
      TRANSACTION_ACTIVITIES_QUERY_KEY,
      transactionNumber,
      "forward",
    ];

    const backwardTraceabilityQueryKey: QueryKey = [
      TRANSACTION_ACTIVITIES_QUERY_KEY,
      transactionNumber,
      "backward",
    ];

    const getDialogTitle = (title: string) => (
      <DialogHeader
        title={title}
        type={transactionType}
        number={transactionNumber}
        translationRoot="transactions"
      />
    );

    const isFetching =
      isFetchingProp ||
      isFetchingTransaction ||
      isFetchingLinkedAdjustment ||
      isFetchingLinkedOriginal ||
      isFetchingCorrections ||
      isRecycleMaterialsFetching;

    return (
      <>
        <main className={styles.transactionDetailsContent}>
          <div className={styles.transactionDetailsContentWrapper}>
            <div className={styles.transactionDetailsNavigation}>
              <LinkButton
                to={returnUrl}
                variant="inline"
                id="request-back-link"
                iconComponent={ReturnIcon}
                className={styles.transactionDetailsBackLink}
              >
                {t("transactions.grid.title")}
              </LinkButton>
              {isWithNavigation && (
                <TransactionNavigation
                  navigationTransactions={navigationTransactions}
                  transactionListInfo={transactionListInfo}
                  isDirectNavigation={isDirectNavigation}
                />
              )}
            </div>
            <div className={styles.transactionDetailsData}>
              {isFetching && <Loader isBlocking />}
              {(transactionType === TransactionType.Delivery ||
                transactionType === TransactionType.Receipt) && (
                <ProcessingStatusBanner transaction={data!} />
              )}
              <TransactionStatus transaction={data!} canAssign={canAssign} />
              <InvoiceLink
                prefix={t("invoices.linkPrefix")}
                invoiceNumber={data?.invoiceNumber}
                className={styles.transactionDetailsInvoice}
              />
              <div id={TRANSACTION_DETAILS_PORTAL_ID} />
              <TransactionContentView
                transaction={data!}
                linkedOriginal={linkedOriginalData}
                linkedAdjustment={linkedAdjustmentData}
                activitiesQueryKey={activitiesQueryKey}
                transactionQueryKey={transactionQueryKey}
                correctionsQueryKey={correctionsQueryKey}
                isCurrentUserAssignee={isCurrentUserAssignee}
                corrections={mappedCorrections}
                isPermitted={isPermitted}
                canFlagManually={canFlagManually}
                canEditManualFlag={canEditManualFlag}
                canUnflag={canUnflag}
                canOverrideRegYear={canOverrideRegYear}
                canEditRetreadingTransaction={canEditRetreadingTransaction}
                dataSectionCallback={
                  data!.transactionType === TransactionType.Retreading
                    ? openDialog
                    : undefined
                }
                materials={materials || []}
                isInitiatedDelivery={isInitiatedDelivery}
                targetCompanyInfo={data?.targetCompanyInfo}
              />
              <TransactionActionsContainer
                transaction={data!}
                isPermitted={isPermitted}
                activitiesQueryKey={activitiesQueryKey}
                isCurrentUserAssignee={isCurrentUserAssignee}
                transactionDetailsQueryKey={transactionQueryKey}
                isLoadingTransaction={isLoading || isFetching}
                isInitiatedDelivery={isInitiatedDelivery}
              />
              <div className={styles.bottomNavigationContainer}>
                {isCurrentUserAssignee &&
                  canOverrideRegYear && (
                    <OverrideRegYear
                      transaction={data!}
                    />
                  )}
                {canUpdateClaimNumber &&
                  (data!.statusCode === TransactionStatusEnum.InReview ||
                    data!.statusCode === TransactionStatusEnum.Approved) &&
                  (data!.transactionType === TransactionType.Delivery ||
                    data!.transactionType === TransactionType.Receipt) && (
                    <ClaimNumberEditButton
                      transactionId={data!.transactionId}
                      currentClaimNumber={data!.claimNumber}
                    />
                  )}
                {canUpdateTrailerNumber &&
                  (data!.statusCode === TransactionStatusEnum.InReview ||
                    data!.statusCode === TransactionStatusEnum.Approved) &&
                  (data!.transactionType === TransactionType.Pickup ||
                    data!.transactionType === TransactionType.Shipment ||
                    data!.transactionType === TransactionType.Delivery ||
                    data!.transactionType === TransactionType.Receipt) && (
                    <TrailerNumberEditButton
                      transactionId={data!.transactionId}
                      currentTrailerNumber={data!.trailerNumber}
                    />
                  )}
                {isWithNavigation && (
                  <div className={styles.bottomNavigation}>
                    <TransactionNavigation
                      navigationTransactions={navigationTransactions}
                      transactionListInfo={transactionListInfo}
                      isDirectNavigation={isDirectNavigation}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        {hasSidebar && (
          <CollapseComponent storageKey="sidebarCollapsed">
            <RecordsSidebar
              objectId={transactionNumber}
              entityType="transaction"
              entityName={t("transactions.details.entityName")}
              initiatorEmail={currentUser?.email || ""}
              getDialogTitle={getDialogTitle}
              fileNamePrefix={t(
                `${transactionType === TransactionType.Adjustment
                  ? "adjustment"
                  : "transactions"
                }.details.export.fileNamePrefix`,
                {
                  transactionNumber,
                }
              )}
              fileNameSuffix=""
              canViewHistory={canViewHistory}
              canExportHistory={canExportHistory}
              activitiesQueryKey={activitiesQueryKey}
              canViewNotes={canViewNotes}
              canAddNote={canAddNote}
              canExportNotes={canExportNotes}
              notesQueryKey={["transactionNotes", transactionId]}
              getNotes={() => api.getTransactionNotes(transactionId)}
              addNote={(text) => {
                return api.addTransactionNote({ transactionId, text });
              }}
              canViewForwardTraceability={
                canSeeLinkedBatches &&
                FORWARD_TRACE_TYPE_AVAILABLE_LIST.includes(transactionType)
              }
              canViewBackwardTraceability={
                canSeeLinkedBatches &&
                BACKWARD_TRACE_TYPE_AVAILABLE_LIST.includes(transactionType)
              }
              forwardTraceabilityQueryKey={forwardTraceabilityQueryKey}
              backwardTraceabilityQueryKey={backwardTraceabilityQueryKey}
              getBackwardTraceability={() => {
                return api.getLinkedTransactions({
                  transactionNumber,
                  isTargetTransactionNext: false,
                });
              }}
              getForwardTraceability={() => {
                return api.getLinkedTransactions({
                  transactionNumber,
                  isTargetTransactionNext: true,
                });
              }}
              transactionOrTransfer={data!}
            />
          </CollapseComponent>
        )}
        {isDialogRendered && (
          <RetreadingTransactionFormDialog
            title={t("transactions.details.editRetreadingTransaction.title")}
            transaction={(data! as unknown) as Retreader}
            isActive={isDialogActive}
            onClose={closeDialog}
            onSubmit={onReTreadingEditSubmit}
            isLoading={isLoading}
            materials={materials || []}
          />
        )}
      </>
    );
  };

  return (
    <div className={styles.transactionDetails}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && data && renderContent()}
      {isError && (
        <NotFoundPage
          returnLink="/transactions"
          title={
            isTransactionNotFound
              ? "transactions.details.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["transactions.details.error.description"]}
          returnLinkDescription="transactions.details.error.returnLink"
        />
      )}
    </div>
  );
};

export default TransactionDetailsView;
